import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LucideIcon } from '../UI/LucideIcon';
import { useFecharPopup, navegarMenu, cliqueSpa } from '../PageTabs/PageTabs';
import { abasPainel, abasEmissao, nomeEmissao, ABAS_INTERNO, ARTEFATOS_INTERNO, SHOW_DASHBOARD_BTG } from '../../services/navAbas';

// Rolou mais que LIMITE_COMPACTA: o menu compacta (`.lev-nav-wrap.compacta`,
// lev.css); só volta ao normal abaixo de LIMITE_EXPANDE. Dois limites de
// propósito: compactar encolhe o menu ~10px, o conteúdo sobe e o navegador
// corrige a rolagem nesses mesmos ~10px (scroll anchoring) — com um limite
// só, parar perto dele fazia o menu ficar compactando/expandindo sem parar.
// A folga (60px) precisa ser bem maior que esse encolhimento.
const LIMITE_COMPACTA = 120;
const LIMITE_EXPANDE = 60;
// Tolerância pra diagonal: sair do item/painel só fecha depois disto.
const ATRASO_FECHAR_MS = 220;

// Item do menu com sub-páginas (Painel, Emissões, Interno) — atalho; a navegação de
// verdade entre sub-páginas são as abas da página (PageTabs). Wrapper com o
// botão do item e o painel como irmãos (botão não pode conter botões).
//  - Mouse: abre ao entrar/focar, fecha ao sair com atraso; clicar no item
//    navega pra primeira sub-página (o mesmo `onNav` de sempre).
//  - Toque: 1º toque abre, 2º navega.
//  - Teclado: ↓ entra no painel, ↑/↓ navegam, Esc fecha e volta pro item;
//    os itens do painel ficam fora do Tab (tabIndex -1), como num menu.
// `grupos`: [{ titulo?, subtitulo?, itens: [{ id, label, icon, path, atual, sub? }] }]
// — `sub` (links externos { id, label, href }) vira submenu lateral (ItemComSubLateral).
function NavItemComSub({ it, ativo, grupos, onNav, onIr }) {
  const [aberto, setAberto] = useState(false);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const painelRef = useRef(null);
  const timerRef = useRef(null);
  const toqueRef = useRef(false);
  const abertoNoToqueRef = useRef(false);
  const suprimirFocoRef = useRef(false);

  const cancelarFechamento = () => clearTimeout(timerRef.current);
  const fechar = useCallback(foco => {
    clearTimeout(timerRef.current);
    setAberto(false);
    // Devolve o foco pro item (Esc) sem que o onFocus reabra o painel.
    if (foco && btnRef.current) { suprimirFocoRef.current = true; btnRef.current.focus(); suprimirFocoRef.current = false; }
  }, []);
  useFecharPopup(aberto, wrapRef, fechar);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const focarPrimeiro = () => requestAnimationFrame(() => painelRef.current?.querySelector('[role="menuitem"]')?.focus());

  return (
    <div ref={wrapRef} className={`lev-nav-item${aberto ? ' aberto' : ''}`}
      // Hover só de mouse/caneta — no toque quem decide é o onClick.
      onPointerEnter={e => { if (e.pointerType === 'touch') return; cancelarFechamento(); setAberto(true); }}
      onPointerLeave={e => { if (e.pointerType === 'touch') return; cancelarFechamento(); timerRef.current = setTimeout(() => setAberto(false), ATRASO_FECHAR_MS); }}
      onFocus={() => { if (suprimirFocoRef.current) return; cancelarFechamento(); setAberto(true); }}
      // Foco saiu do item (Tab). `:hover`: no Safari o clique num item do painel
      // não leva o foco junto (relatedTarget null) — sem isso fecharia antes do clique.
      onBlur={e => { if (!wrapRef.current.contains(e.relatedTarget) && !wrapRef.current.matches(':hover')) setAberto(false); }}>
      <button ref={btnRef} type="button" className={`lev-nav-link${ativo ? ' active' : ''}`}
        aria-haspopup="true" aria-expanded={aberto}
        onPointerDown={e => { toqueRef.current = e.pointerType === 'touch'; abertoNoToqueRef.current = aberto; }}
        onClick={() => {
          if (toqueRef.current && !abertoNoToqueRef.current) { setAberto(true); return; }
          fechar(); onIr(); onNav(it.id);
        }}
        onKeyDown={e => { if (e.key === 'ArrowDown') { e.preventDefault(); setAberto(true); focarPrimeiro(); } }}>
        <LucideIcon name={it.icon} size={16} className="lev-nav-link-icone"/>
        <span>{it.label}</span>
        <LucideIcon name="chevron-down" size={14} className="lev-chevron"/>
      </button>
      <div ref={painelRef} className={`lev-dropdown${aberto ? ' aberto' : ''}`} role="menu" aria-label={it.label}
        onKeyDown={e => navegarMenu(e, painelRef.current)}>
        {grupos.map((g, gi) => (
          <div key={gi} role="group" aria-label={[g.titulo, g.subtitulo].filter(Boolean).join(': ') || it.label}>
            {gi > 0 && <div className="lev-dropdown-divisor" role="separator"/>}
            {g.titulo && <div className="lev-dropdown-grupo" aria-hidden="true">{g.titulo}</div>}
            {g.subtitulo && <div className="lev-dropdown-emissao" aria-hidden="true">{g.subtitulo}</div>}
            {g.itens.map(s => s.sub ? (
              <ItemComSubLateral key={s.id} s={s} onFechar={() => { fechar(); onIr(); }}/>
            ) : (
              <a key={s.id} href={s.path} role="menuitem" tabIndex={-1} className="lev-dropdown-item"
                aria-current={s.atual ? 'page' : undefined}
                onClick={e => { if (!cliqueSpa(e)) return; e.preventDefault(); fechar(); onIr(); s.ir(); }}>
                <LucideIcon name={s.icon} size={16}/>
                {s.label}
              </a>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// Item do dropdown com submenu lateral (ex. "Artefatos" no dropdown do Interno):
// o painel abre ao lado do item e desce a partir dele — aberto por CSS (:hover/
// :focus-within de .lev-dropdown-sub, lev.css), sem estado próprio. Abre pra
// esquerda quando não cabe à direita da tela (medido ao entrar/focar). Clicar
// no item em si navega (`s.ir`); os links do submenu são externos (nova aba).
const LARGURA_SUBMENU = 300;
function ItemComSubLateral({ s, onFechar }) {
  const wrapRef = useRef(null);
  const [esquerda, setEsquerda] = useState(false);
  const medir = () => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (r) setEsquerda(r.right + LARGURA_SUBMENU > window.innerWidth);
  };
  return (
    <div ref={wrapRef} className={`lev-dropdown-sub${esquerda ? ' para-esquerda' : ''}`} onPointerEnter={medir} onFocus={medir}>
      <a href={s.path} role="menuitem" tabIndex={-1} className="lev-dropdown-item" aria-haspopup="true"
        onClick={e => { if (!cliqueSpa(e)) return; e.preventDefault(); onFechar(); s.ir(); }}>
        <LucideIcon name={s.icon} size={16}/>
        {s.label}
        <LucideIcon name="chevron-right" size={14} className="lev-dropdown-sub-seta"/>
      </a>
      <div className="lev-dropdown-flyout" role="menu" aria-label={s.label}>
        {s.sub.map(a => (
          <a key={a.id} href={a.href} target="_blank" rel="noopener noreferrer" role="menuitem" tabIndex={-1}
            className="lev-dropdown-item" onClick={onFechar}>
            <LucideIcon name="claude" size={16}/>
            {a.label}
            <LucideIcon name="external-link" size={13} className="lev-dropdown-externo"/>
          </a>
        ))}
      </div>
    </div>
  );
}

export function isLeverageUser(user) {
  return !!(user && user.email && user.email.toLowerCase().endsWith('@leveragesec.com.br'));
}

// Autorização real do Dash BTG ("Controle de Obrigações") — vem do backend via
// usuario.tem_acesso_btg_dashboard (login) / data.tem_acesso_btg_dashboard
// (/auth/me), já resolvendo tanto equipe Leverage quanto a lista de permissão
// de verdade (Control.PermissaoUsuariosExternos, tipo_identidade=
// 'PORTAL_BTG_OBRIGACOES') — ver authApi.js/buildUser. NÃO é mais domínio de
// e-mail: um @btgpactual.com qualquer não garante acesso, precisa estar na
// lista. É o único critério usado pra decidir o que MOSTRAR (item de menu
// aqui, card em /escolha, RequireBtgAccess em App.jsx) — só uma dica de UI,
// a autorização de verdade continua sendo checada no servidor a cada request
// (GET /v1/btg-dashboard); ver checarBtgDashboardAcesso/DashboardBtg.jsx pra
// o tratamento de 401/403 se essa flag ficar desatualizada dentro da sessão.
// Só site_user tem esse campo (guest/clientes B2B não) — ausente, tratamos
// como sem acesso (não como "ainda não decidido").
// Com SHOW_DASHBOARD_BTG desligada (navAbas.js) é false pra todo mundo.
export function temAcessoBtgDashboard(user) {
  return SHOW_DASHBOARD_BTG && !!(user && user.temAcessoBtgDashboard === true);
}

// Flag temporária pra esconder "Início" do menu sem apagar o código — mesmo
// padrão de SHOW_OBRIGACOES/SHOW_FLUXO_CAIXA (Painel2.jsx). Mude pra `false`
// pra ele não aparecer. Exportada porque App.jsx também usa: com a flag
// desligada, /welcome (link direto) redireciona pro homePath em vez de
// renderizar a página (ver Route "/welcome" lá).
export const SHOW_INICIO = false; // TODO: false pra escondê-lo

// Flag temporária pra uma versão mock do Painel inteiro (Hero + abas, dados
// fixos, sem API) usada pra prototipar funcionalidades novas antes de plugar
// em dados reais. Mesmo padrão de SHOW_INICIO: com a flag desligada, some do
// menu E a rota /painel-mock (App.jsx) redireciona pro homePath em vez de
// renderizar.
export const SHOW_MOCK_PAINEL = false; // TODO: false pra tirar do ar

// `ultimaEmissao`: última emissão aberta na sessão (estado `emissao` do App) —
// vira o grupo "Última acessada" do dropdown de Emissões; null = sem o grupo.
// `visaoBtgPainel`: Painel na visão BTG (SeletorProjeto) — lista as abas dessa
// visão (Enquadramento e Obrigações) no dropdown do Painel.
export default function NavBar({ active, onNav, user, guest, onLogout, onEditPerfil, onCreateAccount, onAjuda, ultimaEmissao = null, visaoBtgPainel = false }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [compacta, setCompacta] = useState(false);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = window.scrollY;
        // Entre os dois limites mantém o estado atual.
        setCompacta(atual => (atual ? y > LIMITE_EXPANDE : y > LIMITE_COMPACTA));
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  const irPara = path => () => { navigate(path); window.scrollTo({ top: 0 }); };
  const subItem = (id, label, icon, path, ir = irPara(path)) => ({ id, label, icon, path, ir, atual: pathname === path });
  const gruposPorItem = {
    painel: [{ itens: abasPainel(visaoBtgPainel).map(a => subItem(a.id, a.label, a.icon, `/painel/${a.slug}`)) }],
    emissoes: [
      { itens: [subItem('todas', 'Todas as emissões', 'layers', '/emissoes/escolha', () => onNav && onNav('emissoes'))] },
      ...(ultimaEmissao ? [{
        titulo: 'Última acessada',
        subtitulo: nomeEmissao(ultimaEmissao),
        itens: abasEmissao(ultimaEmissao, user).map(a => subItem(a.id, a.label, a.icon, `/emissoes/${ultimaEmissao.id}/${a.slug}`)),
      }] : []),
    ],
    // Só aparece pra equipe Leverage (o item "Interno" só existe pra ela).
    interno: [
      { itens: [subItem('todas', 'Todas as ferramentas', 'layout-dashboard', '/interno', () => onNav && onNav('interno'))] },
      { titulo: 'Ferramentas', itens: ABAS_INTERNO.map(a => subItem(a.id, a.label, a.icon, `/interno/${a.slug}`)) },
      // Artefatos do Claude (links externos) — submenu lateral; clicar no
      // item leva pra linha de artefatos em /interno.
      ...(ARTEFATOS_INTERNO.length ? [{ itens: [{ ...subItem('artefatos', 'Artefatos', 'claude', '/interno'), atual: false, sub: ARTEFATOS_INTERNO }] }] : []),
      // /adm é um galho próprio (fora de /interno), mesmo público (equipe Leverage).
      { titulo: 'Administração', itens: [subItem('adm', 'Área Adm', 'key-round', '/adm')] },
    ],
  };
  const items = [
    ...(SHOW_INICIO ? [{ id: 'inicio', label: 'Início', icon: 'home' }] : []),
    { id: 'painel',       label: 'Painel',       icon: 'layout-dashboard' },
    ...(SHOW_MOCK_PAINEL ? [{ id: 'painel-mock', label: 'Painel (mock)', icon: 'flask-conical' }] : []),
    { id: 'emissoes',     label: 'Emissões',     icon: 'layers' },
    // "Preferências" desativada — redundante com Emissões (SHOW_PREFERENCIAS, App.jsx).
    { id: 'apidocs',      label: 'Doc. API',     icon: 'code-2' },
  ];
  // "Adm" (/adm — liberações de acesso privado) não tem item de menu de
  // propósito: pra equipe Leverage, o caminho é o card destacado em /escolha
  // (ver Escolha.jsx), não este NavBar.
  if (isLeverageUser(user)) items.push({ id: 'interno', label: 'Interno', icon: 'lock' });
  if (temAcessoBtgDashboard(user)) items.push({ id: 'dashboard-btg', label: 'Dash. BTG', icon: 'shield-check' });
  return (
    <header className={`lev-nav-wrap${compacta ? ' compacta' : ''}`}>
      <nav className="lev-nav">
        <a className="lev-nav-brand" href="https://www.leveragesec.com.br/">
          <img src="/logo-leverage.svg" alt="Leverage" style={{height:26}} onError={e=>{e.target.style.display='none';}}/>
        </a>
        <div className={`lev-nav-links${open?' open':''}`}>
          {items.map(it=> gruposPorItem[it.id] ? (
            <NavItemComSub key={it.id} it={it} ativo={active===it.id} grupos={gruposPorItem[it.id]}
              onNav={id => onNav && onNav(id)} onIr={() => setOpen(false)}/>
          ) : (
            <button key={it.id} className={`lev-nav-link${active===it.id?' active':''}`}
              onClick={()=>{onNav&&onNav(it.id);setOpen(false);}}>
              <LucideIcon name={it.icon} size={16} className="lev-nav-link-icone"/>
              <span>{it.label}</span>
            </button>
          ))}
        </div>
        <div className="lev-nav-right">
          {onAjuda && (
            <button className="lev-nav-icon lev-nav-ajuda" onClick={onAjuda} title="Central de ajuda" aria-label="Central de ajuda">
              <LucideIcon name="circle-help" size={18}/>
            </button>
          )}
          {user && <>
            <div className="lev-nav-user">
              <div className="lev-nav-avatar">{user.iniciais||'IN'}</div>
              <div className="lev-nav-user-meta">
                <span className="lev-nav-user-name">{user.nome||'Investidor'}</span>
                <span className="lev-nav-user-org">{user.org||'Cliente'}</span>
              </div>
            </div>
            {onEditPerfil && (
              <button className="lev-nav-icon" onClick={onEditPerfil} title="Editar perfil" aria-label="Editar perfil">
                <LucideIcon name="user-pen" size={18}/>
              </button>
            )}
            <button className="lev-nav-icon" onClick={onLogout} title="Sair" aria-label="Sair">
              <LucideIcon name="log-out" size={18}/>
            </button>
          </>}
          {!user && guest && <>
            <div className="lev-nav-user">
              <div className="lev-nav-avatar lev-nav-avatar-guest"><LucideIcon name="user" size={16} color="#fff"/></div>
              <div className="lev-nav-user-meta">
                <span className="lev-nav-user-name">Visitante</span>
                <span className="lev-nav-user-org">Seleção não é salva</span>
              </div>
            </div>
            {onCreateAccount && (
              <button className="lev-nav-icon" onClick={onCreateAccount} title="Criar conta" aria-label="Criar conta">
                <LucideIcon name="user-plus" size={18}/>
              </button>
            )}
            <button className="lev-nav-icon" onClick={onLogout} title="Sair" aria-label="Sair">
              <LucideIcon name="log-out" size={18}/>
            </button>
          </>}
          <button className="lev-nav-burger" onClick={()=>setOpen(o=>!o)} aria-label={open?'Fechar menu':'Abrir menu'} aria-expanded={open}>
            <LucideIcon name={open?'x':'menu'} size={20}/>
          </button>
        </div>
      </nav>
    </header>
  );
}
